import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, API, TOKEN_KEY, formatApiErrorDetail } from "@/api/client";
import { toast } from "sonner";
import { Trash2, Mail, Phone, FileDown } from "lucide-react";

const statusOptions = ["novo", "em contacto", "reunião marcada", "fechado", "arquivado"];

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/leads"); setLeads(data); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try { await api.patch(`/admin/leads/${id}`, { status }); toast.success("Estado atualizado"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminar este contacto?")) return;
    try { await api.delete(`/admin/leads/${id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const downloadValuationPdf = async (id, name) => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const res = await fetch(`${API}/admin/leads/${id}/valuation.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `avaliacao-${(name || "cliente").toLowerCase().replace(/\s+/g, "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Erro a gerar PDF.");
    }
  };

  const filtered = leads.filter((l) => !filter || l.lead_type === filter);

  return (
    <AdminLayout>
      <div data-testid="admin-leads">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-3xl text-stone-900">Contactos recebidos</h1>
            <p className="mt-2 text-stone-600">Todos os pedidos submetidos pelo site.</p>
          </div>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="leads-filter" className="rounded-full border border-stone-200 bg-white px-4 py-2 text-sm">
            <option value="">Todos os tipos</option>
            <option value="contacto">Contacto geral</option>
            <option value="comprador">Comprador</option>
            <option value="vendedor">Vendedor</option>
            <option value="avaliacao">Avaliação</option>
          </select>
        </div>

        <div className="mt-8 space-y-3">
          {loading ? <div className="text-stone-500">A carregar...</div> :
           filtered.length === 0 ? <div className="card-soft p-10 text-center text-stone-500">Sem contactos ainda.</div> :
           filtered.map((l) => (
            <div key={l.id} data-testid={`lead-${l.id}`} className="card-soft p-5">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="font-medium text-stone-900">{l.name}</div>
                    <span className="tag-chip">{l.lead_type}</span>
                    {l.zone && <span className="tag-chip">{l.zone}</span>}
                  </div>
                  <div className="mt-2 flex items-center gap-4 text-sm text-stone-600">
                    <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1 link-underline"><Mail className="h-3.5 w-3.5" /> {l.email}</a>
                    {l.phone && <a href={`tel:${l.phone}`} className="inline-flex items-center gap-1 link-underline"><Phone className="h-3.5 w-3.5" /> {l.phone}</a>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <select value={l.status || "novo"} onChange={(e) => updateStatus(l.id, e.target.value)} data-testid={`lead-status-${l.id}`} className="rounded-lg border border-stone-200 bg-white px-3 py-1.5 text-sm">
                    {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <button onClick={() => remove(l.id)} data-testid={`lead-delete-${l.id}`} className="btn-ghost text-red-700"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              {l.message && <p className="mt-3 text-sm text-stone-700 whitespace-pre-wrap">{l.message}</p>}
              {l.lead_type === "avaliacao" && (
                <div className="mt-3 grid sm:grid-cols-2 md:grid-cols-4 gap-2 text-xs text-stone-600">
                  {l.address && <div><span className="text-stone-400">Morada:</span> {l.address}</div>}
                  {l.property_type && <div><span className="text-stone-400">Tipo:</span> {l.property_type}</div>}
                  {l.typology && <div><span className="text-stone-400">Tipologia:</span> {l.typology}</div>}
                  {l.area && <div><span className="text-stone-400">Área:</span> {l.area}m²</div>}
                  {l.condition && <div><span className="text-stone-400">Estado:</span> {l.condition}</div>}
                </div>
              )}
              {l.created_at && <div className="mt-2 text-xs text-stone-400">Recebido: {new Date(l.created_at).toLocaleString("pt-PT")}</div>}
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}
